import {
  Printer,
  Wifi,
  Usb,
  Bluetooth,
  Cloud,
  Bot,
  Receipt,
  FileText,
  Tag,
  Settings,
} from "lucide-react";

// ─── Template Groups ─────────────────────────────────────────────────────────

export const ASSIGNABLE_TEMPLATE_GROUPS = [
  { id: "all", label: "All Types" },
  { id: "sales", label: "Sales & Receipts", groupName: "Sales & Receipts" },
  { id: "repairs", label: "Repairs & Service", groupName: "Repairs & Service" },
  { id: "barcodes", label: "Barcodes & Labels", groupName: "Barcodes & Labels" },
  { id: "purchasing", label: "Purchasing & Inventory", groupName: "Purchasing & Inventory" },
  { id: "reports", label: "Reports", groupName: "Reports" },
];

// ─── Template Types Definition ───────────────────────────────────────────────

export const ASSIGNABLE_TEMPLATE_TYPES = [
  // Sales & Receipts (3)
  {
    value: "sales_receipt",
    label: "Sales Receipt",
    group: "Sales & Receipts",
    description: "Thermal receipt printed at POS checkout / sale completion",
    recommendedTarget: "pos_thermal",
  },
  {
    value: "sales_invoice",
    label: "Sales Invoice",
    group: "Sales & Receipts",
    description: "Standard sales invoice document with items and tax breakdown",
    recommendedTarget: "office_laser",
  },
  {
    value: "quotation",
    label: "Quotation",
    group: "Sales & Receipts",
    description: "Customer price quotation estimate",
    recommendedTarget: "office_laser",
  },

  // Repairs & Service (3)
  {
    value: "repair_job_note",
    label: "Repair Job Note",
    group: "Repairs & Service",
    description: "Customer intake work order / job intake sheet",
    recommendedTarget: "office_laser",
  },
  {
    value: "repair_job_label",
    label: "Repair Job Label",
    group: "Repairs & Service",
    description: "Barcode / tracking sticker printed on label roll for the device",
    recommendedTarget: "label_barcode",
  },
  {
    value: "repair_invoice",
    label: "Repair Invoice",
    group: "Repairs & Service",
    description: "Final billing invoice for repair service & parts",
    recommendedTarget: "office_laser",
  },

  // Barcodes & Labels (2)
  {
    value: "product_barcode",
    label: "Product Barcode",
    group: "Barcodes & Labels",
    description: "Product barcode tag / shelf edge label",
    recommendedTarget: "label_barcode",
  },
  {
    value: "serial_barcode",
    label: "Serial Barcode",
    group: "Barcodes & Labels",
    description: "Unique serialized inventory item barcode sticker",
    recommendedTarget: "label_barcode",
  },

  // Purchasing & Inventory (3)
  {
    value: "purchase_order",
    label: "Purchase Order",
    group: "Purchasing & Inventory",
    description: "Purchase order document sent to suppliers",
    recommendedTarget: "office_laser",
  },
  {
    value: "goods_received_note",
    label: "Goods Received Note",
    group: "Purchasing & Inventory",
    description: "Inventory intake / delivery confirmation document",
    recommendedTarget: "office_laser",
  },
  {
    value: "stock_transfer_document",
    label: "Stock Transfer Document",
    group: "Purchasing & Inventory",
    description: "Inter-store inventory transfer manifest",
    recommendedTarget: "office_laser",
  },

  // Reports (11)
  {
    value: "product_stock_report",
    label: "Product Stock Report",
    group: "Reports",
    description: "Inventory stock level status report",
    recommendedTarget: "office_laser",
  },
  {
    value: "sales_summary_report",
    label: "Sales Summary Report",
    group: "Reports",
    description: "Aggregated store sales figures",
    recommendedTarget: "office_laser",
  },
  {
    value: "parts_usage_report",
    label: "Parts Usage Report",
    group: "Reports",
    description: "Service parts consumption report",
    recommendedTarget: "office_laser",
  },
  {
    value: "daily_cash_balance_report",
    label: "Daily Cash Balance Report",
    group: "Reports",
    description: "End-of-day register drawer reconciliation",
    recommendedTarget: "office_laser",
  },
  {
    value: "expense_report",
    label: "Expense Report",
    group: "Reports",
    description: "Operating expenditures report",
    recommendedTarget: "office_laser",
  },
  {
    value: "profit_loss_report",
    label: "Profit & Loss Report",
    group: "Reports",
    description: "Financial P&L statement",
    recommendedTarget: "office_laser",
  },
  {
    value: "stock_transfer_report",
    label: "Stock Transfer Report",
    group: "Reports",
    description: "Summary report of stock movements between stores",
    recommendedTarget: "office_laser",
  },
  {
    value: "customerwise_sales_report",
    label: "Customer-wise Sales Report",
    group: "Reports",
    description: "Sales breakdown organized by customer account",
    recommendedTarget: "office_laser",
  },
  {
    value: "supplierwise_expense_report",
    label: "Supplier-wise Expense Report",
    group: "Reports",
    description: "Procurement costs organized by vendor",
    recommendedTarget: "office_laser",
  },
  {
    value: "inventory_movement_report",
    label: "Inventory Movement Report",
    group: "Reports",
    description: "Detailed stock in/out audit ledger",
    recommendedTarget: "office_laser",
  },
  {
    value: "sales_invoice_report",
    label: "Sales Invoice Report",
    group: "Reports",
    description: "Master invoice register and tax journal",
    recommendedTarget: "office_laser",
  },
];

// ─── Connection Types ────────────────────────────────────────────────────────

export const CONNECTION_TYPES = [
  { value: "network", label: "Network (IP / LAN)", icon: Wifi, placeholder: "192.168.1.100:9100" },
  { value: "usb", label: "USB Direct", icon: Usb, placeholder: "USB001" },
  { value: "bluetooth", label: "Bluetooth", icon: Bluetooth, placeholder: "00:11:22:33:44:55" },
  { value: "cloud", label: "Cloud Service", icon: Cloud, placeholder: "cloud-printer-id" },
  { value: "agent", label: "ERF Print Agent", icon: Bot, placeholder: "Local Agent Printer" },
];

export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const PAPER_SIZES = [
  "custom",
  "a4",
  "letter",
  "legal",
  "a5",
  "a6",
  "b5",
  "thermal_80",
  "thermal_58",
];

export const STANDARD_DIMENSIONS = {
  a4: { width: 210, height: 297, unit: "mm" },
  letter: { width: 8.5, height: 11, unit: "inch" },
  legal: { width: 8.5, height: 14, unit: "inch" },
  a5: { width: 148, height: 210, unit: "mm" },
  a6: { width: 105, height: 148, unit: "mm" },
  b5: { width: 176, height: 250, unit: "mm" },
  thermal_80: { width: 3.15, height: 0, unit: "inch" },
  thermal_58: { width: 2.28, height: 0, unit: "inch" },
};

export const PAPER_SIZE_OPTIONS = PAPER_SIZES.map((s) => ({
  value: s,
  label:
    s === "custom"
      ? "Custom Size"
      : s === "thermal_80"
        ? "80mm Thermal (Continuous)"
        : s === "thermal_58"
          ? "58mm Thermal (Continuous)"
          : s.toUpperCase(),
}));

export const ORIENTATIONS = [
  { value: "portrait", label: "Portrait (Vertical)" },
  { value: "landscape", label: "Landscape (Horizontal)" },
];

export const UNITS = [
  { value: "mm", label: "Millimeters (mm)" },
  { value: "inch", label: "Inches (in)" },
  { value: "cm", label: "Centimeters (cm)" },
];

// ─── 1-Click Paper Size Presets ─────────────────────────────────────────────

export const PAPER_SIZE_PRESETS = [
  {
    id: "80mm_thermal",
    label: "80mm Thermal Receipt",
    shortLabel: "80mm Thermal",
    size: "custom",
    width: 3.15,
    height: 0,
    unit: "inch",
    orientation: "portrait",
    description: "Standard POS receipt continuous roll (3.15\" / 80mm)",
    badge: "Receipts",
  },
  {
    id: "58mm_thermal",
    label: "58mm Thermal Receipt",
    shortLabel: "58mm Thermal",
    size: "custom",
    width: 2.28,
    height: 0,
    unit: "inch",
    orientation: "portrait",
    description: "Compact POS receipt continuous roll (2.28\" / 58mm)",
    badge: "Mini Receipts",
  },
  {
    id: "a4",
    label: "A4 Document",
    shortLabel: "A4 Sheet",
    size: "a4",
    width: 210,
    height: 297,
    unit: "mm",
    orientation: "portrait",
    description: "Standard office laser / inkjet sheet (210 × 297 mm)",
    badge: "Invoices & Reports",
  },
  {
    id: "letter",
    label: "Letter Document",
    shortLabel: "Letter Sheet",
    size: "letter",
    width: 8.5,
    height: 11,
    unit: "inch",
    orientation: "portrait",
    description: "Standard US office sheet (8.5 × 11 in)",
    badge: "US Standard",
  },
  {
    id: "a5",
    label: "A5 Half Sheet",
    shortLabel: "A5 Sheet",
    size: "a5",
    width: 148,
    height: 210,
    unit: "mm",
    orientation: "portrait",
    description: "Half-size invoices, work orders, vouchers (148 × 210 mm)",
    badge: "Half Sheet",
  },
  {
    id: "label_50_25",
    label: "Barcode Label (50×25mm)",
    shortLabel: "50×25mm Label",
    size: "custom",
    width: 50,
    height: 25,
    unit: "mm",
    orientation: "portrait",
    description: "Adhesive device / product barcode sticker",
    badge: "Barcode Labels",
  },
  {
    id: "custom",
    label: "Custom Dimensions",
    shortLabel: "Custom",
    size: "custom",
    width: "",
    height: "",
    unit: "mm",
    orientation: "portrait",
    description: "Specify custom width, height, and unit manually",
    badge: "Manual",
  },
];

// ─── 1-Click Printer Archetypes for Setup Wizard ─────────────────────────────

export const PRINTER_ARCHETYPES = [
  {
    id: "pos_thermal",
    label: "POS Receipt Printer",
    shortLabel: "Thermal Receipt",
    icon: Receipt,
    defaultName: "Counter Thermal Printer",
    connection: "network",
    paperPresetId: "80mm_thermal",
    defaultAssignments: ["sales_receipt"],
    description: "Prints checkout customer receipts on an 80mm continuous thermal roll",
    highlight: "Auto-configures 80mm paper & Sales Receipt template",
  },
  {
    id: "office_laser",
    label: "Office Document Printer",
    shortLabel: "Laser / Inkjet",
    icon: FileText,
    defaultName: "Office Laser Printer",
    connection: "network",
    paperPresetId: "a4",
    defaultAssignments: ["sales_invoice", "quotation", "repair_invoice", "repair_job_note"],
    description: "Prints invoices, quotations, job notes, and reports on A4/Letter paper",
    highlight: "Auto-configures A4 paper & Invoices/Quotes templates",
  },
  {
    id: "label_barcode",
    label: "Barcode & Label Printer",
    shortLabel: "Label Roll",
    icon: Tag,
    defaultName: "Label Printer",
    connection: "usb",
    paperPresetId: "label_50_25",
    defaultAssignments: ["product_barcode", "repair_job_label", "serial_barcode"],
    description: "Prints sticker labels for repair devices and inventory barcodes",
    highlight: "Auto-configures 50×25mm label roll & Barcode templates",
  },
  {
    id: "custom",
    label: "Custom Printer Setup",
    shortLabel: "Custom",
    icon: Settings,
    defaultName: "",
    connection: "network",
    paperPresetId: null,
    defaultAssignments: [],
    description: "Register a standalone printer and configure paper trays manually",
    highlight: "Manual setup",
  },
];

export const BLANK_PRINTER_FORM = {
  store_id: null,
  name: "",
  connection_type: "network",
  address: "",
  status: "active",
  is_default: false,
  meta: [],
  archetypeId: "pos_thermal",
  autoCreatePaper: true,
};

export const BLANK_PAPER_FORM = {
  printer_id: "",
  label: "",
  size: "custom",
  width: 3.15,
  height: 0,
  unit: "inch",
  orientation: "portrait",
  is_default: false,
  meta: [],
  presetId: "80mm_thermal",
};

export const getConnectionIcon = (type) => {
  const found = CONNECTION_TYPES.find((c) => c.value === type);
  return found?.icon ?? Printer;
};

export const statusVariant = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "secondary";
    default:
      return "outline";
  }
};

/**
 * Invalidate all printer-related queries across the app to force a fresh refetch from backend.
 * Refetches active queries immediately and marks inactive queries stale so they refetch on display.
 */
export const invalidateAllPrinterQueries = async (queryClient) => {
  if (!queryClient) return;
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["printers"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["all-paper-sizes"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["printer-paper-sizes"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["all-paper-size-assignments"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["paper-size-templates"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["templates"], refetchType: "all" }),
  ]);
};

